import { usePathname } from 'next/navigation'
import clsx from 'clsx'
import { useAtom } from 'jotai'
import { Trans, useTranslation } from 'react-i18next'
import { ButtonNextLink } from '@/components/ButtonNextLink'
import { DetailsList } from '@/components/DetailsList/DetailsList'
import { CampaignCard } from '@/components/InfoCard/InfoCard'
import { useProductMetadata } from '@/components/LayoutWithMenu/productMetadataHooks'
import { Pillow } from '@/components/Pillow/Pillow'
import { SelectInsuranceGrid } from '@/components/SelectInsuranceGrid/SelectInsuranceGrid'
import { TextWithLink } from '@/components/TextWithLink'
import { TotalPrice } from '@/components/TotalPrice/TotalPrice'
import { BUNDLE_DISCOUNT_PERCENTAGE } from '@/features/bundleDiscount/bundleDiscount.constants'
import { getBundleDiscountOnCurrentOffer } from '@/features/bundleDiscount/bundleDiscount.utils'
import { BundleDiscountProgress } from '@/features/bundleDiscount/components/BundleDiscountProgress/BundleDiscountProgress'
import { useShopSessionValueOrThrow } from '@/services/shopSession/ShopSessionContext'
import { useTracking } from '@/services/Tracking/useTracking'
import type { Money } from '@/utils/formatter'
import { getSummarisedPrice } from '@/utils/getOfferPrice'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import { useBreakpoint } from '@/utils/useBreakpoint/useBreakpoint'
import { useFormatter } from '@/utils/useFormatter'
import { StoryblokComponent } from '@storyblok/react'
import { Badge } from 'ui/src/components/Badge/Badge'
import { Button } from 'ui/src/components/Button/Button'
import { IconButton } from 'ui/src/components/Button/IconButton'
import { Card } from 'ui/src/components/Card/Card'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { FullscreenDialog } from 'ui/src/components/Dialog/FullscreenDialog'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { ToggleTip } from 'ui/src/components/Tooltip/ToggleTip'
import { CloverIcon } from 'ui/src/icons/CloverIcon'
import { InfoIcon } from 'ui/src/icons/InfoIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { tokens } from 'ui/src/theme/tokens.css'
import { visuallyHidden } from 'ui/src/utils/visibility.css'
import { priceCalculatorAddedOffer, usePurchaseSummaryAtom } from '../priceCalculatorAtoms'
import { usePriceTemplate } from '../priceTemplateAtom'
import { ribbon } from './PurchaseSymmary.css'

export function PurchaseSummary({ className }: { className?: string }) {
  const shopSession = useShopSessionValueOrThrow()
  const isDesktop = useBreakpoint('lg')
  const { t } = useTranslation(['purchase-form', 'cart', 'common'])
  const locale = useRoutingLocale()
  const currentPath = usePathname()
  const [addedOffer, setAddedOffer] = useAtom(priceCalculatorAddedOffer)
  const priceTemplate = usePriceTemplate()
  const tracking = useTracking()
  const purchaseSummaryContent = usePurchaseSummaryAtom()
  const products = useProductMetadata()
  const { eligibleProductCount } = shopSession.cart.bundleDiscount

  if (!addedOffer) {
    return null
  }

  const handleAddMore = () => {
    // Price intent is reset whenever a product is added to the cart so the only thing
    // we need to do in order to add another product is to close the purchase summary
    setAddedOffer(null)
  }

  const handleGoToCart = () => {
    tracking.reportBeginCheckout(shopSession.cart)
  }

  const handleProductClick = (pathname: string) => {
    // If the clicked link matches the current path (member wants to add another product of the same type),
    // close the purchase summary and go to the price calculator for the selected product
    if (pathname === currentPath) {
      setAddedOffer(null)
    }
  }

  // Rely on priceTemplate.addMultiple if we show "Add another cat" or "Add another dog" button
  const showAddMultipleButton = addedOffer.product.multiple && priceTemplate.addMultiple

  const bundleDiscountOnCurrentOffer = getBundleDiscountOnCurrentOffer(addedOffer)
  // Show the Bundle Discount Potential Savings for an eligible product for a purchase that
  // doesn't qualify for bundle discount yet
  const shouldShowBundleDiscountInfoCard =
    !bundleDiscountOnCurrentOffer && addedOffer.bundleDiscount.potentialYearlySavings != null
  const shouldShowBundleDiscountRibbon = bundleDiscountOnCurrentOffer != null

  // Animate when the added product is eligible for bundle discount (even if it's the first one)
  const shouldAnimateBundleProgress = addedOffer.bundleDiscount.isEligible

  return (
    <div className={className}>
      <div className={clsx(yStack({ gap: { _: 'xxl', md: 'xxxl' } }))}>
        <div className={clsx(yStack({ gap: 'lg' }))}>
          <Card.Root>
            {shouldShowBundleDiscountRibbon && (
              <Card.Ribbon className={ribbon}>
                <CloverIcon size="1rem" color={tokens.colors.signal.green.element} />
                <Text as="p" size="xs" color="signalGreenText" align="center" singleLine>
                  {t('cart:BUNDLE_DISCOUNT_RIBBON', { percentage: BUNDLE_DISCOUNT_PERCENTAGE })}
                </Text>
              </Card.Ribbon>
            )}

            <div className={yStack({ gap: 'lg' })}>
              <Card.Header>
                <Card.Media>
                  <Pillow size="small" {...addedOffer.product.pillowImage} />
                </Card.Media>
                <Card.Heading>
                  <Card.Title>
                    {isDesktop ? addedOffer.product.displayNameFull : addedOffer.product.displayNameShort}
                  </Card.Title>
                  <Card.Subtitle>
                    {addedOffer.product.name === 'SE_PAYMENT_PROTECTION' && addedOffer.insuranceAmount
                      ? addedOffer.insuranceAmount.displayName
                      : addedOffer.exposure.displayNameShort}
                  </Card.Subtitle>
                </Card.Heading>

                <Card.Aside>
                  <Badge variant="grey1">{t('common:ADDED')}</Badge>
                </Card.Aside>
              </Card.Header>
            </div>

            <Card.Divider />

            <div className={yStack({ gap: 'xxxs' })}>
              <TotalPrice label={t('YOUR_PRICE', { ns: 'common' })} {...getSummarisedPrice(addedOffer.cost)} />

              {bundleDiscountOnCurrentOffer?.isPending && (
                <DetailsList.Root size="xs" color="textSecondary">
                  <DetailsList.Item>
                    <DetailsList.Label>{bundleDiscountOnCurrentOffer.itemDiscount.displayName}</DetailsList.Label>
                    <DetailsList.Value className={xStack({ gap: 'xxs' })}>
                      {bundleDiscountOnCurrentOffer.itemDiscount.displayValue}
                      {bundleDiscountOnCurrentOffer.itemDiscount.tooltip && (
                        <ToggleTip>
                          <Text size="xs" color="textNegative" align="center">
                            {bundleDiscountOnCurrentOffer.itemDiscount.tooltip}
                          </Text>
                        </ToggleTip>
                      )}
                    </DetailsList.Value>
                  </DetailsList.Item>
                </DetailsList.Root>
              )}
            </div>

            {shouldShowBundleDiscountInfoCard && (
              <InfoCard savings={addedOffer.bundleDiscount.potentialYearlySavings!} />
            )}
          </Card.Root>

          <div className={yStack({ gap: 'xs' })}>
            {showAddMultipleButton && (
              <Button variant="secondary" size="large" onClick={handleAddMore}>
                {t('ADD_MULTIPLE_INSURANCE_LABEL', {
                  productName: addedOffer.product.displayNameShort.toLowerCase(),
                })}
              </Button>
            )}
            {!showAddMultipleButton && (
              <Dialog.Root>
                <Dialog.Trigger asChild>
                  <Button variant="secondary" size="large">
                    {t('ADD_ANOTHER_INSURANCE_LABEL')}
                  </Button>
                </Dialog.Trigger>
                <FullscreenDialog.Modal accessibleCloseLabel={t('common:DIALOG_CLOSE')}>
                  <Dialog.Title className={visuallyHidden}>{t('common:SELECT_INSURANCE')}</Dialog.Title>
                  <SelectInsuranceGrid
                    products={products ?? []}
                    heading={t('common:SELECT_INSURANCE')}
                    onProductClick={handleProductClick}
                  />
                </FullscreenDialog.Modal>
              </Dialog.Root>
            )}
            <ButtonNextLink
              href={PageLink.checkout({ locale }).toRelative()}
              onClick={handleGoToCart}
              variant="primary"
              size="large"
            >
              {t('cart:PURCHASE_SUMMARY_GO_TO_CART_LABEL')}
            </ButtonNextLink>
          </div>
        </div>

        <section className={yStack({ gap: 'lg' })}>
          <div className={yStack({ gap: 'md' })}>
            <div className={xStack({ alignItems: 'center', gap: 'xs', justifyContent: 'space-between' })}>
              <Heading as="h2" color="textPrimary" variant="standard.24">
                {shopSession.cart.cost.hasBundleDiscount
                  ? t('BUNDLE_DISCOUNT_SECTION_TITLE_EXISTING_DISCOUNT')
                  : t('BUNDLE_DISCOUNT_SECTION_TITLE')}
              </Heading>

              <ToggleTip
                Trigger={
                  <IconButton
                    data-no-hover
                    Icon={<InfoIcon size="1.5rem" color={tokens.colors.fills.opaque.secondary} />}
                  />
                }
              >
                <TextWithLink size="xs" color="textNegative" align="center" href={PageLink.bundleDiscount({ locale })}>
                  {t('BUNDLE_DISCOUNT_SECTION_TOOLTIP', { percentage: BUNDLE_DISCOUNT_PERCENTAGE })}
                </TextWithLink>
              </ToggleTip>
            </div>

            {!!eligibleProductCount && (
              <BundleDiscountProgress
                eligibleInsuranceCount={eligibleProductCount}
                shouldAnimate={shouldAnimateBundleProgress}
              />
            )}
          </div>

          <div className={sprinkles({ marginBottom: 'lg' })}>
            {purchaseSummaryContent?.map((blok) => (
              <StoryblokComponent key={blok._uid} blok={blok} nested={true} />
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

function InfoCard({ savings }: { savings: Money }) {
  const formatter = useFormatter()

  const potentialYearlySavings = formatter.money(savings)

  return (
    <CampaignCard>
      <Text color="signalGreenText" size="sm">
        <Trans
          i18nKey="BUNDLE_DISCOUNT_POTENTIAL_SAVING"
          ns="cart"
          components={{ strong: <strong className={sprinkles({ color: 'textPrimary' })} /> }}
          values={{
            percentage: BUNDLE_DISCOUNT_PERCENTAGE,
            potentialSaving: potentialYearlySavings,
          }}
        />
      </Text>
    </CampaignCard>
  )
}
